import { useFormik } from "formik";
import { noop } from "lodash";
import { useEffect } from "react";
import { connect, type ConnectedProps } from "react-redux";

import { bindAction } from "../../../../interfaces";
import { successFactorValidationSchema } from "../../../../utils/validationSchemas/successFactorValidationSchema";
import { type AppDispatch, type RootState } from "../../../Application/globaltypes/redux";
import { integrationBaseSelector } from "../state/selectors";
import { reset } from "../state/slices/integrationsOverviewSlice";
import { fetchPacksAction } from "../state/thunks/integrationPacksThunk";
import { type SfIntegrationConfigs } from "../types/successFactors";
import { SfConfigurationForm } from "./Forms/SfConfigurationForm";

export interface SfIntegrationConfigurationProps {
  integrationName: string;
  onUpdate: (configs: SfIntegrationConfigs) => void;
  initialConfig: SfIntegrationConfigs;
  enableReinitialize?: boolean;
  renderHeader?: boolean;
  onIsValidChange: (isValid: boolean) => void;
  bindResetForm?: (reset: () => void) => void;
  accountId: number;
}

export const SfIntegrationConfiguration = (props: SfIntegrationConfigurationProps & PropsFromRedux) => {
  const {
    onUpdate,
    initialConfig,
    enableReinitialize,
    integrationName,
    isLoading,
    packs,
    fetchPacks,
    resetPacks,
    renderHeader,
    onIsValidChange,
    bindResetForm,
    accountId,
  } = props;

  const formik = useFormik({
    validationSchema: successFactorValidationSchema,
    initialValues: initialConfig,
    enableReinitialize: !!enableReinitialize,
    onSubmit: noop,
    validateOnBlur: false,
  });

  const { values, isValid, validateForm, resetForm } = formik;

  useEffect(() => {
    validateForm();
  }, [validateForm]);

  useEffect(() => {
    bindResetForm?.(resetForm);
  }, [bindResetForm, resetForm]);

  useEffect(() => {
    onUpdate(values);
  }, [values, onUpdate]);

  useEffect(() => {
    onIsValidChange(isValid);
  }, [isValid, onIsValidChange]);

  useEffect(() => {
    fetchPacks(accountId);
    return resetPacks;
  }, [fetchPacks, resetPacks, accountId]);

  return (
    <SfConfigurationForm
      formik={formik}
      integrationName={integrationName}
      isPacksLoading={isLoading}
      packs={packs}
      renderHeader={renderHeader}
    />
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const base = integrationBaseSelector(state).base;
  return {
    isLoading: base.packs.isLoading,
    packs: base.packs.items,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    fetchPacks: bindAction(fetchPacksAction, dispatch),
    resetPacks: bindAction(reset, dispatch),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(SfIntegrationConfiguration);

export default ConnectedComponent;
